/**
 * The Geotagged Photos map layer.
 *
 * Every organisation has geotagged media, so this layer is not optional — but it
 * used to be a hand-authored `mapLayers` entry in each legacy customer's
 * `config.json`. A provisioned organisation's config comes from the backend and
 * has no such entry, so newly created orgs uploaded geotagged photos and saw
 * nothing on the map and no layer in the panel.
 *
 * It is now synthesised at adapt time, the same way CM-table layers are
 * generated by `useCmTableMapLayers`, so no one has to add it. An authored layer
 * with the same id still wins, and the marker icon can be overridden per org.
 */

import { sharedIconPath } from "./sharedIcons";

/** Layer id. The media endpoint's `mapLayerId` points at this exact string. */
export const MEDIA_LAYER_ID = "Geotagged Photos";

/** Endpoint the layer's GeoJSON comes from. */
export const MEDIA_LAYER_URL = "api/v2/media/geojson/";

/** Shipped marker, served from the app's shared icon library. */
export const DEFAULT_MEDIA_MARKER_ICON = sharedIconPath("media-marker.png");

/** `ui_config` key an admin's chosen marker icon is stored under. */
export const MEDIA_MARKER_ICON_KEY = "geotaggedPhotosIcon";

// Markers grow with zoom so they stay findable when zoomed out without covering
// the map when zoomed in; labels fade in only once the map is close enough to read.
const ICON_SIZE = ["step", ["zoom"], 0.2, 11, 0.3, 13, 0.4];
const LABEL_SIZE = ["interpolate", ["linear"], ["zoom"], 11, 0, 12, 8, 14, 12];
const LABEL_OPACITY = ["step", ["zoom"], 0, 12, 0.7];
const LABEL_OFFSET = [0, -1.5];
const LABEL_COLOR = "#ffffff";

/**
 * The canonical layer, optionally with a custom marker icon.
 *
 * Carries both `style` (the legacy renderer path) and `geostyler` (preferred by
 * `transformLayerConfigs` when present) built from the same values, so whichever
 * path runs produces the same map.
 */
export const buildGeotaggedPhotosLayer = (icon: string = DEFAULT_MEDIA_MARKER_ICON): Record<string, any> => {
  const image = icon || DEFAULT_MEDIA_MARKER_ICON;
  return {
    id: MEDIA_LAYER_ID,
    url: MEDIA_LAYER_URL,
    type: "geojson",
    geometry: "symbol",
    useOAuth: true,
    defaultIcon: image,
    initialVisibility: true,
    style: {
      iconSize: ICON_SIZE,
      labelSize: LABEL_SIZE,
      labelColor: LABEL_COLOR,
      labelField: "{name}",
      labelOffset: LABEL_OFFSET,
      labelOpacity: LABEL_OPACITY
    },
    geostyler: {
      name: MEDIA_LAYER_ID,
      rules: [
        {
          name: "point",
          symbolizers: [
            { kind: "Icon", size: ICON_SIZE, image },
            {
              kind: "Text",
              size: LABEL_SIZE,
              color: LABEL_COLOR,
              label: "{{name}}",
              offset: LABEL_OFFSET,
              opacity: LABEL_OPACITY
            }
          ]
        }
      ]
    },
    popupFields: ["name", "description", "media_type"],
    descriptionField: "description"
  };
};

/**
 * Guarantees the layer is present, without disturbing an org that already has
 * one. An authored entry wins outright — that is the escape hatch for an org
 * that wants different styling than the icon override covers.
 */
export const withGeotaggedPhotosLayer = <T extends { id?: string }>(layers: T[], icon?: string): T[] => {
  if (layers.some((layer) => layer?.id === MEDIA_LAYER_ID)) return layers;
  return [...layers, buildGeotaggedPhotosLayer(icon) as unknown as T];
};
